import React, { useEffect, useState } from 'react'
import api from '../../../services/authService'
import SeccionNormatividad from '../../../componentes/panel-institucion/SeccionNormatividad'
import { Users, Briefcase, GraduationCap, ArrowRight, Ship, Layers, Globe, Search } from 'lucide-react'
import { UserGroupIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

interface Miembro {
  id_miembro: number
  rol_sistema: string
  estado?: string
}

interface Grupo {
  id_grupo: number
  miembros_count?: number
}

interface ResumenInstitucion {
  vista: 'gestion'
  constitucion: {
    id_constitucion: number
    nombre: string
    tipo?: string
  }
  grupos: Grupo[]
  miembros: Miembro[]
  estadisticas_miembros: {
    total_miembros: number
    miembros_activos: number
    docentes: number
    directivos: number
    alumnos: number
    total_grupos: number
  }
}

const PaginaPanelJefes: React.FC = () => {
  const [miembros, setMiembros] = useState<Miembro[]>([])
  const [grupos, setGrupos] = useState<Grupo[]>([])
  const [resumen, setResumen] = useState<ResumenInstitucion | null>(null)
  const [, setCargando] = useState(false)

  const cargarDatos = async () => {
    setCargando(true)
    try {
      const resumenRes = await api.get('/api/institucion/dashboard/resumen')
      setResumen(resumenRes.data || null)
      setMiembros(resumenRes.data?.miembros || [])
      setGrupos(resumenRes.data?.grupos || [])
    } catch (error) {
      console.error("Error cargando datos", error)
      // toast.error("Error al cargar datos") // Opcional para no saturar al usuario si falla algo menor
    } finally {
      setCargando(false)
    }
  }

  useEffect(() => {
    cargarDatos()
  }, [])

  // Clasificación de miembros para estadísticas
  const maestrosCount = resumen?.estadisticas_miembros.docentes ?? miembros.filter(m => m.rol_sistema === 'institucion_maestro').length
  const estudiantesCount = resumen?.estadisticas_miembros.alumnos ?? miembros.filter(m => !['institucion_jefe', 'institucion_admin', 'institucion_maestro'].includes(m.rol_sistema)).length

  return (
    <div className="space-y-8 pb-10 min-h-screen bg-slate-50 dark:bg-[#0a0a0a] -m-4 lg:-m-8 p-4 lg:p-8">
      <div className="flex items-center justify-between">
        <div>
            <h1 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-slate-900 to-slate-500 bg-clip-text text-transparent dark:from-white dark:to-zinc-400 tracking-tight">Panel Institucional</h1>
            <p className="mt-1 text-sm font-medium text-slate-500 dark:text-zinc-400">
              {resumen?.constitucion?.nombre || 'Resumen general y accesos rápidos'}
            </p>
        </div>
      </div>

      {/* Tarjetas de Estadísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {[
          {
            titulo: 'Total Miembros',
            valor: resumen?.estadisticas_miembros.total_miembros ?? miembros.length,
            icono: Users,
            color: 'from-blue-500 to-cyan-500',
            shadow: 'shadow-blue-500/20'
          },
          {
            titulo: 'Estudiantes',
            valor: estudiantesCount,
            icono: GraduationCap,
            color: 'from-emerald-400 to-green-600',
            shadow: 'shadow-emerald-500/20'
          },
          {
            titulo: 'Docentes',
            valor: maestrosCount,
            icono: Briefcase,
            color: 'from-fuchsia-500 to-purple-600',
            shadow: 'shadow-fuchsia-500/20'
          },
          {
            titulo: 'Grupos',
            valor: resumen?.estadisticas_miembros.total_grupos ?? grupos.length,
            icono: UserGroupIcon,
            color: 'from-indigo-400 to-blue-600',
            shadow: 'shadow-indigo-500/20'
          }
        ].map((stat, i) => (
          <div key={i} className="group relative overflow-hidden rounded-3xl border border-slate-200/50 bg-white/70 p-6 shadow-sm backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5">
            <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br opacity-20 blur-2xl transition-all group-hover:scale-150 group-hover:opacity-30 dark:opacity-10 dark:group-hover:opacity-20"></div>
            <div className="flex items-center gap-5 relative z-10">
              <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${stat.color} text-white shadow-lg ${stat.shadow}`}>
                <stat.icono className="h-7 w-7" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-zinc-400">{stat.titulo}</p>
                <p className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mt-1">{stat.valor}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Hubs de Operación Institucional (Despacho, Almacén, Simulaciones y LIGIE) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Hub 1: Despacho y Terminal Portuaria */}
        <Link
          to="/institucion/despacho"
          className="group relative overflow-hidden rounded-3xl border border-cyan-500/25 bg-gradient-to-br from-cyan-500/10 via-white/80 to-transparent dark:from-cyan-500/10 dark:via-white/[0.03] dark:to-transparent p-6 shadow-sm backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-cyan-500/50 flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border border-cyan-500/25">
                PORT & TOS
              </span>
              <div className="p-2.5 rounded-2xl bg-cyan-600 text-white shadow-md shadow-cyan-500/30 group-hover:scale-110 transition-transform">
                <Ship className="h-5 w-5" />
              </div>
            </div>
            <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
              Despacho y Terminal Portuaria
            </h3>
            <p className="mt-2 text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
              Consola de supervisión de exportación e importación vía SSA Manzanillo, bookings, Anexo 29, pesaje VGM y asignación Emisor-Receptor.
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200/60 dark:border-white/5 flex items-center justify-between text-xs font-bold text-cyan-600 dark:text-cyan-400">
            <span>Abrir Consola Portuaria</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>

        {/* Hub 2: Almacén de Prácticas 3D */}
        <Link
          to="/institucion/almacen-practica"
          className="group relative overflow-hidden rounded-3xl border border-blue-500/25 bg-gradient-to-br from-blue-500/10 via-white/80 to-transparent dark:from-blue-500/10 dark:via-white/[0.03] dark:to-transparent p-6 shadow-sm backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-blue-500/50 flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/25">
                WMS 3D
              </span>
              <div className="p-2.5 rounded-2xl bg-blue-600 text-white shadow-md shadow-blue-500/30 group-hover:scale-110 transition-transform">
                <Layers className="h-5 w-5" />
              </div>
            </div>
            <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              Gemelo Digital WMS
            </h3>
            <p className="mt-2 text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
              Simulador interactivo de racks selectivos, cálculo de peso en toneladas, rotación FIFO/FEFO y etiquetado térmico GS1.
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200/60 dark:border-white/5 flex items-center justify-between text-xs font-bold text-blue-600 dark:text-blue-400">
            <span>Entrar al Almacén 3D</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>

        {/* Hub 3: Casos y Simulaciones */}
        <Link
          to="/institucion/simulaciones"
          className="group relative overflow-hidden rounded-3xl border border-indigo-500/25 bg-gradient-to-br from-indigo-500/10 via-white/80 to-transparent dark:from-indigo-500/10 dark:via-white/[0.03] dark:to-transparent p-6 shadow-sm backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-indigo-500/50 flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/25">
                PEDAGOGÍA
              </span>
              <div className="p-2.5 rounded-2xl bg-indigo-600 text-white shadow-md shadow-indigo-500/30 group-hover:scale-110 transition-transform">
                <Globe className="h-5 w-5" />
              </div>
            </div>
            <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              Casos y Simulaciones
            </h3>
            <p className="mt-2 text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
              Diseño, asignación de tareas a grupos escolares y matriz de evaluación del rendimiento práctico de alumnos.
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200/60 dark:border-white/5 flex items-center justify-between text-xs font-bold text-indigo-600 dark:text-indigo-400">
            <span>Gestionar Casos</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>

        {/* Hub 4: Inteligencia Aduanera LIGIE */}
        <Link
          to="/institucion/tarifas/clasificacion"
          className="group relative overflow-hidden rounded-3xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/10 via-white/80 to-transparent dark:from-emerald-500/10 dark:via-white/[0.03] dark:to-transparent p-6 shadow-sm backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-emerald-500/50 flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-wider bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/25">
                LIGIE 2026
              </span>
              <div className="p-2.5 rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/30 group-hover:scale-110 transition-transform">
                <Search className="h-5 w-5" />
              </div>
            </div>
            <h3 className="text-base font-black text-slate-900 dark:text-white tracking-tight group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
              Catálogo Arancelario
            </h3>
            <p className="mt-2 text-xs text-slate-600 dark:text-zinc-400 leading-relaxed font-medium">
              Consulta de fracciones arancelarias a 10 dígitos (NICO), tasas IGI/IVA y regulaciones no arancelarias para despachos.
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-200/60 dark:border-white/5 flex items-center justify-between text-xs font-bold text-emerald-600 dark:text-emerald-400">
            <span>Consultar LIGIE</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        </Link>
      </div>

      {/* Banner de Acción Rápida */}
      <div className="group relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 p-8 md:p-10 text-white shadow-2xl shadow-indigo-500/25 transition-all hover:shadow-indigo-500/40">
        <div className="absolute -right-10 -top-10 h-64 w-64 rounded-full bg-white/10 blur-3xl transition-transform duration-700 group-hover:scale-150"></div>
        <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-blue-400/20 blur-2xl"></div>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 p-8 opacity-10 transition-transform duration-500 group-hover:scale-110 group-hover:opacity-20 hidden md:block">
            <Users size={200} />
        </div>
        
        <div className="relative z-10 max-w-2xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-blue-100 backdrop-blur-md border border-white/10">
              <span className="h-2 w-2 rounded-full bg-blue-400 animate-pulse"></span> Panel de Control
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 tracking-tight">Gestión Integral de Miembros</h2>
            <p className="text-blue-100/90 mb-8 text-lg font-medium leading-relaxed max-w-xl">Administra estudiantes, docentes y personal administrativo, así como la asignación de grupos de trabajo de manera centralizada y eficiente.</p>
            <Link to="/institucion/gestion-miembros" className="inline-flex items-center gap-3 bg-white text-indigo-700 px-7 py-3.5 rounded-xl font-bold hover:bg-slate-50 hover:scale-105 transition-all shadow-xl shadow-black/10">
                Ir a Gestión de Miembros <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </Link>
        </div>
      </div>

      {/* Sección Normativa */}
      <div className="pt-6">
        <SeccionNormatividad basePath="/institucion/jefes/normas" />
      </div>
    </div>
  )
}

export default PaginaPanelJefes
